import type { CalendarDay, CalendarHours, CalendarSession } from './calendar-types';

export const SNAP_MINUTES = 10;
export const MINUTES_PER_DAY = 24 * 60;
export const MIN_ROW_PX = 24;
export const HALF_HOUR_AXIS_PX_PER_HOUR = 96;

export interface SessionSpan {
  start: number;
  end: number;
  slotMinutes: number;
  capacity: number;
}

export interface SlotRow {
  start: number;
  end: number;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

export function minutesBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / MS_PER_MINUTE);
}

export function minuteOf(day: Pick<CalendarDay, 'startsAt'>, iso: string): number {
  return minutesBetween(day.startsAt, iso);
}

export function isoAtMinute(day: Pick<CalendarDay, 'startsAt'>, minute: number): string {
  return new Date(Date.parse(day.startsAt) + minute * MS_PER_MINUTE).toISOString();
}

export function snapMinute(minute: number, step: number = SNAP_MINUTES): number {
  return Math.round(minute / step) * step;
}

export function clampMinute(minute: number, hours: CalendarHours, length: number): number {
  const latest = Math.max(hours.fromMinute, hours.toMinute - length);
  return Math.min(Math.max(minute, hours.fromMinute), latest);
}

export function slotStep(slotMinutes: number | undefined, fallback: number = SNAP_MINUTES): number {
  return slotMinutes && Number.isFinite(slotMinutes) && slotMinutes > 0 ? Math.round(slotMinutes) : fallback;
}

export function sessionSpan(day: Pick<CalendarDay, 'startsAt'>, session: Pick<CalendarSession, 'startsAt' | 'endsAt' | 'slotMinutes' | 'capacity'>): SessionSpan {
  return { start: minuteOf(day, session.startsAt), end: minuteOf(day, session.endsAt), slotMinutes: slotStep(session.slotMinutes), capacity: Math.max(session.capacity, 1) };
}

export function providerSpans(day: Pick<CalendarDay, 'date' | 'startsAt'>, sessions: readonly CalendarSession[]): SessionSpan[] {
  return sessions.filter((session) => session.date === day.date).map((session) => sessionSpan(day, session));
}

export function slotRows(span: Pick<SessionSpan, 'start' | 'end' | 'slotMinutes'>): SlotRow[] {
  const step = slotStep(span.slotMinutes);
  const rows: SlotRow[] = [];
  for (let start = span.start; start < span.end; start += step) rows.push({ start, end: Math.min(start + step, span.end) });
  return rows;
}

export function spanAt(spans: readonly SessionSpan[], minute: number): SessionSpan | undefined {
  return spans.find((span) => minute >= span.start && minute < span.end);
}

export function snapToSessions(minute: number, spans: readonly SessionSpan[], fallback: number = SNAP_MINUTES): number {
  const span = spanAt(spans, minute);
  if (!span) return snapMinute(minute, fallback);
  const step = slotStep(span.slotMinutes, fallback);
  const snapped = span.start + Math.round((minute - span.start) / step) * step;
  return Math.min(Math.max(snapped, span.start), span.end);
}

export function rowPxPerHour(density: number, slotLengths: readonly number[]): number {
  const shortest = slotLengths.reduce((least, length) => Math.min(least, slotStep(length)), Infinity);
  if (!Number.isFinite(shortest)) return density;
  return Math.max(density, (MIN_ROW_PX * 60) / shortest);
}

export function axisMarks(hours: CalendarHours, pxPerHour: number): number[] {
  const step = pxPerHour >= HALF_HOUR_AXIS_PX_PER_HOUR ? 30 : 60;
  const marks: number[] = [];
  const first = Math.ceil(hours.fromMinute / step) * step;
  for (let minute = first; minute < hours.toMinute; minute += step) marks.push(minute);
  return marks;
}

export function minuteLabel(minute: number): string {
  const wrapped = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour = Math.floor(wrapped / 60);
  const rest = wrapped % 60;
  return `${String(hour).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

export function hourMarks(hours: CalendarHours): number[] {
  const marks: number[] = [];
  const first = Math.ceil(hours.fromMinute / 60) * 60;
  for (let minute = first; minute < hours.toMinute; minute += 60) marks.push(minute);
  return marks;
}

export function minuteToPx(minute: number, hours: CalendarHours, pxPerHour: number): number {
  return ((minute - hours.fromMinute) / 60) * pxPerHour;
}

export function pxToMinute(px: number, hours: CalendarHours, pxPerHour: number): number {
  return hours.fromMinute + (px / pxPerHour) * 60;
}

export function gridHeight(hours: CalendarHours, pxPerHour: number): number {
  return ((hours.toMinute - hours.fromMinute) / 60) * pxPerHour;
}

export function shiftIsoDate(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export function weekStart(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const sinceMonday = (weekday + 6) % 7;
  return shiftIsoDate(date, -sinceMonday);
}

export function rangeFor(date: string, view: 'day' | 'week'): { from: string; days: number } {
  return view === 'week' ? { from: weekStart(date), days: 7 } : { from: date, days: 1 };
}

export function dayIndexAt(days: CalendarDay[], nowIso: string): number {
  const now = Date.parse(nowIso);
  return days.findIndex((day) => Date.parse(day.startsAt) <= now && now < Date.parse(day.endsAt));
}

export function nowMinute(days: CalendarDay[], nowIso: string): { dayIndex: number; minute: number } | null {
  const dayIndex = dayIndexAt(days, nowIso);
  if (dayIndex === -1) return null;
  return { dayIndex, minute: Math.floor((Date.parse(nowIso) - Date.parse(days[dayIndex].startsAt)) / MS_PER_MINUTE) };
}

export function overlapsDay(day: Pick<CalendarDay, 'startsAt' | 'endsAt'>, startsAt: string, endsAt: string): boolean {
  return Date.parse(startsAt) < Date.parse(day.endsAt) && Date.parse(endsAt) > Date.parse(day.startsAt);
}

export function daysSpanned(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / MS_PER_DAY);
}

export function durationLabel(minutes: number): string {
  if (minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
